/**
 * i18n utilities for translation handling, flattening, merging, and conflict resolution
 */

/**
 * Flattens a nested object into dot-notation keys
 * Example: { Web: { Labels: { Menu: "Menu" } } } → { "Web.Labels.Menu": "Menu" }
 */
export function flattenMessages(obj: Record<string, any>, prefix = ''): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined) {
      // Skip null/undefined values
      continue;
    } else if (typeof value === 'string') {
      result[fullKey] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      // Convert primitives to strings
      result[fullKey] = String(value);
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Recursively flatten nested objects
      const nested = flattenMessages(value, fullKey);
      Object.assign(result, nested);
    }
    // Arrays and other types are skipped
  }

  return result;
}

/**
 * Removes conflicting keys: if a key is both a leaf value AND has child keys,
 * removes the leaf value to allow nested structure to work properly
 * Example: if you have {"Web": "value", "Web.Labels.Menu": "Menu"}
 * removes "Web" so the nested structure can be created
 */
export function removeConflictingKeys(flat: Record<string, string>): Record<string, string> {
  const result = { ...flat };
  const allKeys = Object.keys(result);

  // Find and remove parent keys that also have children
  for (const key of allKeys) {
    const hasChildren = allKeys.some(k => k !== key && k.startsWith(key + '.'));

    if (hasChildren) {
      delete result[key];
    }
  }

  return result;
}

/**
 * Checks if an object appears to be a nested translation structure
 * (vs. already flat with dot-notation keys)
 */
export function isNestedObject(obj: Record<string, any>): boolean {
  if (!obj || typeof obj !== 'object') return false;

  // Check if has any object values (sign of nesting)
  for (const value of Object.values(obj)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      return true;
    }
  }

  return false;
}

/**
 * Converts flat dot-notation keys back to nested structure
 * Example: { "Web.Labels.Menu": "Menu" } → { Web: { Labels: { Menu: "Menu" } } }
 * Useful for merging with next-intl's expected nested structure
 *
 * Handles conflicts: if a parent key is already a string value, skips creating nested structure
 * (parent string value takes precedence over nested children)
 */
export function unflattenMessages(flat: Record<string, string>): Record<string, any> {
  const result: Record<string, any> = {};

  for (const [key, value] of Object.entries(flat)) {
    const parts = key.split('.');
    let current = result;
    let canNest = true;

    // Traverse/create the path, but stop if we hit a non-object
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];

      // If we encounter a string value at this position, we can't nest further
      // Skip this key to avoid conflicts
      if (typeof current[part] === 'string') {
        canNest = false;
        break;
      }

      if (!(part in current)) {
        current[part] = {};
      }

      // Only continue if it's an object
      if (typeof current[part] === 'object' && current[part] !== null) {
        current = current[part];
      } else {
        canNest = false;
        break;
      }
    }

    // Only set the value if we could safely navigate to it
    if (canNest) {
      const lastPart = parts[parts.length - 1];
      current[lastPart] = value;
    }
  }

  return result;
}

/**
 * Deep merge utility for combining translation structures
 * Recursively merges objects, with later sources overwriting earlier ones
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: (Record<string, any> | undefined)[]
): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        deepMerge(target[key], source[key]);
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return deepMerge(target, ...sources);
}

/**
 * Helper: checks if a value is a plain object (not array, null, etc.)
 */
function isObject(item: any): item is Record<string, any> {
  return item && typeof item === 'object' && !Array.isArray(item);
}

/**
 * Sanitizes a translation message by:
 * 1. Converting {{variable}} to {variable} for ICU compatibility (react-intl expects single braces for variables)
 * 2. Decoding common HTML entities (e.g., &copy; -> ©)
 */
export function sanitizeMessage(message: string): string {
  if (!message || typeof message !== 'string') return message;

  const htmlEntities: Record<string, string> = {
    '&copy;': '©',
    '&reg;': '®',
    '&trade;': '™',
    '&nbsp;': ' ',
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
  };

  return message
    .replace(/\{\{([^}]+)\}\}/g, '{$1}')
    .replace(/&[a-z0-9]+;/gi, match => htmlEntities[match.toLowerCase()] || match);
}
